const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.5;

class EuGuaranteeViewport extends HTMLElement {
  #pointers = new Map();
  #zoom = MIN_ZOOM;
  #pinchDistance = 0;
  #zoomable = false;
  #modal = null;
  #modalObserver = null;
  #graphic = null;

  #onResize = () => this.#syncZoomMode();
  #onModalClick = (event) => this.#handleModalClick(event);
  #onPointerDown = (event) => this.#handlePointerDown(event);
  #onPointerMove = (event) => this.#handlePointerMove(event);
  #onPointerUp = (event) => this.#releasePointer(event);
  #onWheel = (event) => this.#handleWheel(event);
  #onDblClick = (event) => this.#handleDblClick(event);
  #onKeyDown = (event) => this.#handleKeyDown(event);

  connectedCallback() {
    this.#graphic =
      this.querySelector("[data-eu-guarantee-graphic]") ||
      this.querySelector("img.eu-guarantee-modal__graphic") ||
      this.querySelector("img");

    this.#modal = this.closest("gtc-modal");

    this.addEventListener("pointerdown", this.#onPointerDown);
    this.addEventListener("pointermove", this.#onPointerMove);
    this.addEventListener("pointerup", this.#onPointerUp);
    this.addEventListener("pointercancel", this.#onPointerUp);
    this.addEventListener("wheel", this.#onWheel, { passive: false });
    this.addEventListener("dblclick", this.#onDblClick);
    this.addEventListener("keydown", this.#onKeyDown);

    window.addEventListener("resize", this.#onResize, { passive: true });

    if (this.#modal) {
      this.#modal.addEventListener("click", this.#onModalClick);
      this.#modalObserver = new MutationObserver(() => this.#onModalOpenChange());
      this.#modalObserver.observe(this.#modal, {
        attributes: true,
        attributeFilter: ["open"],
      });
      this.#onModalOpenChange();
    }

    this.#syncZoomMode();
  }

  disconnectedCallback() {
    this.removeEventListener("pointerdown", this.#onPointerDown);
    this.removeEventListener("pointermove", this.#onPointerMove);
    this.removeEventListener("pointerup", this.#onPointerUp);
    this.removeEventListener("pointercancel", this.#onPointerUp);
    this.removeEventListener("wheel", this.#onWheel);
    this.removeEventListener("dblclick", this.#onDblClick);
    this.removeEventListener("keydown", this.#onKeyDown);

    window.removeEventListener("resize", this.#onResize);

    if (this.#modal) {
      this.#modal.removeEventListener("click", this.#onModalClick);
    }

    this.#modalObserver?.disconnect();
    this.#modalObserver = null;
    this.#modal = null;
    this.#graphic = null;
    this.#pointers.clear();
  }

  #clamp(value) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  }

  #setZoom(next, focalX, focalY) {
    const graphic = this.#graphic;
    if (!graphic) return;

    const target = this.#clamp(next);
    if (target === this.#zoom) return;

    const rect = this.getBoundingClientRect();
    const x = focalX ?? rect.left + rect.width / 2;
    const y = focalY ?? rect.top + rect.height / 2;
    const offsetX = x - rect.left;
    const offsetY = y - rect.top;
    const ratio = target / this.#zoom;

    this.#zoom = target;
    graphic.style.width = `${this.#zoom * 100}%`;

    this.scrollLeft = (this.scrollLeft + offsetX) * ratio - offsetX;
    this.scrollTop = (this.scrollTop + offsetY) * ratio - offsetY;
  }

  #resetZoom() {
    const graphic = this.#graphic;
    if (!graphic) return;

    this.#zoom = MIN_ZOOM;
    graphic.style.width = "100%";
    this.scrollTo(0, 0);
  }

  #syncZoomMode() {
    const next = getComputedStyle(this).touchAction === "none";
    if (next === this.#zoomable) return;

    this.#zoomable = next;
    if (!this.#zoomable) this.#resetZoom();
  }

  #onModalOpenChange() {
    if (!this.#modal) return;

    if (this.#modal.hasAttribute("open")) {
      this.#syncZoomMode();
    } else {
      this.#resetZoom();
    }
  }

  #distanceBetweenPointers() {
    const [a, b] = Array.from(this.#pointers.values());
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  #midpointOfPointers() {
    const [a, b] = Array.from(this.#pointers.values());
    if (!a || !b) return null;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  #handlePointerDown(event) {
    if (!this.#zoomable) return;

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.setPointerCapture(event.pointerId);

    if (this.#pointers.size === 2) {
      this.#pinchDistance = this.#distanceBetweenPointers();
    }
  }

  #handlePointerMove(event) {
    const previous = this.#pointers.get(event.pointerId);
    if (!previous) return;

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size >= 2) {
      const distance = this.#distanceBetweenPointers();
      const midpoint = this.#midpointOfPointers();

      if (this.#pinchDistance > 0 && distance > 0 && midpoint) {
        this.#setZoom(
          this.#zoom * (distance / this.#pinchDistance),
          midpoint.x,
          midpoint.y,
        );
      }

      this.#pinchDistance = distance;
      return;
    }

    this.scrollLeft -= event.clientX - previous.x;
    this.scrollTop -= event.clientY - previous.y;
  }

  #releasePointer(event) {
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size < 2) this.#pinchDistance = 0;
  }

  #handleWheel(event) {
    if (!this.#zoomable) return;

    if (event.ctrlKey) {
      event.preventDefault();
      this.#setZoom(
        this.#zoom * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP),
        event.clientX,
        event.clientY,
      );
      return;
    }

    event.preventDefault();
    this.scrollTop += event.deltaY;
    this.scrollLeft += event.deltaX;
  }

  #handleDblClick(event) {
    if (!this.#zoomable) return;

    if (this.#zoom > MIN_ZOOM) {
      this.#resetZoom();
      return;
    }

    this.#setZoom(this.#zoom * ZOOM_STEP * 2, event.clientX, event.clientY);
  }

  #handleKeyDown(event) {
    if (!this.#zoomable) return;

    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      this.#setZoom(this.#zoom * ZOOM_STEP);
    } else if (event.key === "-") {
      event.preventDefault();
      this.#setZoom(this.#zoom / ZOOM_STEP);
    } else if (event.key === "0") {
      event.preventDefault();
      this.#resetZoom();
    }
  }

  #handleModalClick(event) {
    const zoomButton = event.target.closest("[data-eu-guarantee-zoom]");
    if (!this.#zoomable || !zoomButton || !this.#modal?.contains(zoomButton)) {
      return;
    }

    const action = zoomButton.dataset.euGuaranteeZoom;

    if (action === "in") this.#setZoom(this.#zoom * ZOOM_STEP);
    else if (action === "out") this.#setZoom(this.#zoom / ZOOM_STEP);
    else this.#resetZoom();
  }
}

if (!customElements.get("eu-guarantee-viewport")) {
  customElements.define("eu-guarantee-viewport", EuGuaranteeViewport);
}
